import { type FC, type ReactElement } from "react";

import { Card, CardRowSimple, ColoredLabel, type GridCard, HStack } from "widgets";

import { getVariantEffectColor } from "../../../app-utils/color.js";
import { generateVariantStyles } from "../../../app-utils/track-shapes/shape-variant.js";
import { type Variant } from "../../../graphql.js";
import { ColoredSequence } from "../../blast-result/ColoredSequence.js";
import { SequenceType } from "../../blast-result/SensorAlnInfoCard.js";
import { VariantSymbol } from "../../genome-viewer/variants-track/VariantSymbol.js";

export interface IVariantCardProps extends GridCard {
  variant: Variant;
}

const VariantCard: FC<IVariantCardProps> = (props) => {
  const variant: Variant = props.variant;

  if (!variant) {
    return null;
  }

  const symbolStyle = generateVariantStyles(variant.variantType).symbolElementStyle;

  return (
    <Card title={"Variant Info"}>
      <CardRowSimple label={"Name"} value={variant.name} />
      <CardRowSimple label={"Preferred name"} value={variant.preferredName} />
      <CardRowSimple label={"Description"} value={variant.description} />
      <CardRowSimple
        label={"Type"}
        value={
          <HStack>
            <VariantSymbol styleContent={symbolStyle} type={variant.variantType} width={12} />
            {`${variant.variantType}`}
          </HStack>
        }
      />
      <CardRowSimple label={"Effect"}>
        {variant.effect && <ColoredLabel color={getVariantEffectColor(variant.effect)}>{variant.effect}</ColoredLabel>}
      </CardRowSimple>
      <CardRowSimple label={"Location class"} value={<>{variant.locationClass}</>} />
      <CardRowSimple label={"Ref allele"}>{getColoredAllele(variant.refAllele)}</CardRowSimple>
      <CardRowSimple label={"Alt allele"}>{getColoredAllele(variant.altAllele)}</CardRowSimple>
      <CardRowSimple label={"Effective allele"}>{getColoredAllele(variant.allele)}</CardRowSimple>
      <CardRowSimple label={"Genome analysis group"} value={variant.variantAnalysisGroup.name} />
      <CardRowSimple label={"Quality"} value={variant.quality} />
      <CardRowSimple label={"Coverage"} value={variant.coverage} />
      <CardRowSimple label={"Zygosity"} value={variant.zygosity} />
    </Card>
  );
};

function getColoredAllele(allele: string): ReactElement {
  return !allele || allele.includes(" ") ? (
    <span style={{ color: "grey" }}>{allele}</span>
  ) : (
    <ColoredSequence offset={1} type={SequenceType.DNA} seq={allele} />
  );
}

export { VariantCard };
